/* eslint-disable max-len */
import { ajaxRequest, getAction, getActionAjax, getThrottleAjax, ModalModule, ObjectToString, t } from "@comact/crc";
import _ from "lodash";
import React from "react";
import { v4 as uuid } from "uuid";
import { convertNodeToServer, IBasicNode, IBasicNodes, IDetailedNodes, INode, INodeServer, isOutdatedDesiredProperty, normalizeHierarchyAndStatus } from "./model";
import { getDetailedNodeById } from "./selectors";
import { actions } from "./slices";

export const fetchAllNodes = getThrottleAjax(() => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => m.fetchAllNodes()),
        url: "/api/v2/nodes/partial",
        timeout: [15 * 1000, 30 * 1000, 30 * 1000],
        onSuccess: (basicNodesFromServer: IBasicNode[]) => {
            const basicNodes: IBasicNodes = _.keyBy(basicNodesFromServer, ({ id }) => id);
            dispatch(actions.setPartial(normalizeHierarchyAndStatus<IBasicNode>(basicNodes)));
            return basicNodes;
        },
    })
), 60 * 1000);

export const getAliases = getThrottleAjax(() => (dispatch) => ajaxRequest({
    url: "/api/v2/nodes/aliases",
    onSuccess: (aliases: Record<string, string> | null) => {
        dispatch(actions.setAliases(aliases));
        return aliases;
    },
}), 60 * 1000);

export const saveAlias = getAction((nodeId: string, alias: string | null) => (dispatch) => (
    ajaxRequest({
        url: `/api/v2/nodes/${nodeId}/alias`,
        method: "PUT",
        data: { alias },
        onSuccess: () => dispatch(actions.setAlias({ nodeId, alias })),
    }).promise
));

export const fetchTopNodes = getThrottleAjax(() => (dispatch, getState) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => m.fetchAllNodes().filter((node) => ["millCodec", "location", "customer"].includes(node.templateName))),
        url: "/api/v2/nodes/org-sites-and-mills",
        timeout: [15 * 1000, 30 * 1000, 30 * 1000],
        onSuccess: (nodesFromServer: INodeServer[]): IDetailedNodes => {
            dispatch(actions.patch(nodesFromServer));
            return getState().detailedNodes;
        },
    })
), 60 * 1000);

export const fetchOneNode = getThrottleAjax((nodeId: string) => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => m.fetchAllNodes().find((node) => node.id == nodeId)),
        url: `/api/v2/nodes/${nodeId}`,
        onSuccess: (nodeFromServer: INodeServer) => {
            dispatch(actions.patch([nodeFromServer]));
        },
    })
), 1 * 1000);

export const saveNodeLinks = getAction((node: Partial<INode>) => async (dispatch) => (
    process.env.EXEC_MODE == "cmoc"
        ? dispatch(saveNode(node)) // for the cmoc, we use the same request for the regular save process
        : ajaxRequest({
            serverLessResolve: () => ({ ...node, modificationDate: Date.now() }),
            url: `/api/v2/nodes/${node.id}`,
            method: "PATCH",
            data: node,
            onSuccess: (nodeFromServer: INodeServer) => dispatch(actions.patch([nodeFromServer])),
        }).promise
));

export const saveNode = getAction((node: Partial<INode>) => async (dispatch, getState) => (
    node.id
        ? ajaxRequest({ // Update existing node
            serverLessResolve: () => _.assign({}, getDetailedNodeById(getState(), node.id), { ...node, modificationDate: Date.now() }) as INode,
            url: `/api/v2/nodes/${node.id}/desired`,
            method: "PATCH",
            data: ((() => {
                const originalNode = getDetailedNodeById(getState(), node.id);
                return _.chain(node.desired)
                    // only modified or outdated properties
                    .pickBy((_value, property) => !_.isEqual(
                        (node.desired as { [key: typeof property]: typeof _value; })?.[property],
                        (node as { [key: typeof property]: typeof _value; })?.[property]
                    ) || isOutdatedDesiredProperty(originalNode, property))
                    .mapValues((value, property) => ({
                        property,
                        value,
                        timestamp: Date.now(),
                    }))
                    .values()
                    .value();
            })()),
            onSuccess: (nodeFromServer: INodeServer) => {
                dispatch(actions.patch([nodeFromServer]));
                return nodeFromServer; // need to be return for the form
            },
        }).promise
        : ajaxRequest({ // Creates a new node
            serverLessResolve: () => ({ ...node, modificationDate: Date.now(), id: uuid() }),
            url: "/api/v2/nodes",
            method: "POST",
            data: ((() => {
                const { reported, ...serverNode } = convertNodeToServer(node as INode); // find a way for convertNodeToServer to use Partial<INode>
                return { ...serverNode, reported: {} }; // remove the reported properties
            })()),
            onSuccess: (nodeFromServer: INodeServer) => {
                dispatch(actions.patch([nodeFromServer]));
                return nodeFromServer; // need to be return for the form
            },
        }).promise
));

export const deleteNode = getAction((id: string) => (dispatch) => (
    ajaxRequest({
        serverLessResolve: () => import("./mocks").then((m) => ({ ...m.fetchNode(id) })), // this won't do anything in serverless
        url: `/api/v2/nodes/${id}`,
        method: "DELETE",
        onSuccess: () => dispatch(actions.deleteRecursive(id)),
    }).promise
));

export const sendCommand = getActionAjax((nodeId: string, command: string) => (_dispatch) => (
    ajaxRequest({
        url: `/api/v2/nodes/${nodeId}/command/${command}`,
        method: "POST",
        showAjaxLoading: true,
        timeout: 10 * 1000,
        onSuccess: () => {
            ModalModule.alert({
                title: t("cloudAgent.rebootCommandModal.title"),
                description: t("cloudAgent.rebootCommandModal.description"),
            });
        },
        onError(e) {
            ModalModule.alert({
                title: t("cloudAgent.rebootCommandModal.title"),
                description: (
                    <>
                        {t("cloudAgent.rebootCommandModal.descriptionFailed")}
                        {e?.request.response ? <ObjectToString data={e?.request.response} /> : t("cloudAgent.rebootCommandModal.descriptionNoReason")}
                    </>
                ),
            });
        },
    })
));
